<template>
  <div class="audioPlayer">
    <div :class="{'isDisabled': isPlaylistActive}">
      <canvas id="arc" class="arc" ref="arc" width="58" height="58"></canvas>
      <transition name="slide-fade" mode="out-in">
        <a
          title="Play/Pause Song"
          class="playerButtons"
          :class="[currentlyStopped ? 'stopped' : (currentlyPlaying ? 'playing' : 'pausing')]"
          @click="togglePlayButton()"
        >
          <span class="play" :class="[currentlyStopped ? 'icon-headphone' : (currentlyPlaying ? 'icon-pause' : 'icon-play_arrow')]"></span>
        </a>
      </transition>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'

export default {
  props: {
    playlist: {
      type: Array,
      default: function() {
        return []
      }
    }
  },

  data () {
    return {
      audio: [],
      currentlyPlaying: false,
      currentlyStopped: true,
      currentTime: 0,
      checkingCurrentPositionInTrack: "",
      trackDuration: [],
      totalDuration: 0,
      proceededTime: 0,
      currentProgressBar: 0,
      isPlaylistActive: false,
      currentSong: 0,
      debug: true,
      audioFile: "",
      interval: 2000,
      displayType: "total time", // "total time" or "track time"

      canvas: null,
      context: null,
      x: 0,
      y: 0,
      radius: 30,
      endPercent: 100,
      curPerc: 0,
      counterClockwise: false,
      circ: Math.PI * 2,
      quart: Math.PI / 2,
    }
  },

  computed: {
    ...mapGetters([
      'isAudioPlaying',
    ]),
  },

  watch: {
    playlist: function (val, oldVal) {
      if(JSON.stringify(val) !== JSON.stringify(oldVal)){
        this.stopAudio()
        this.loadSongs()
      }
    },
		currentTime: function() {
			this.currentTime = Math.round(this.currentTime);
    },
    isAudioPlaying (newValue, oldValue) { // eslint-disable-line no-unused-vars
      if(this.currentlyPlaying && !newValue){
        this.pauseAudio()
      }
    }
  },

  filters: {
		fancyTimeFormat: function(s) {
			return (s - (s %= 60)) / 60 + (9 < s ? ":" : ":0") + s;
		}
  },

	mounted: function() {
    // this.setDisplay('total time')
    this.drawCircle()
    this.loadSongs()
    this.setAudioStatus({ playing: false })

  },

  beforeDestroy: function() {
    this.pauseAudio()
    for (const song of this.audio) {
      song.removeEventListener("ended", this.handleEnded);
      song.removeEventListener("loadedmetadata", this.handleEnded);
    }
		clearTimeout(this.checkingCurrentPositionInTrack);
	},

	methods: {
    ...mapActions([
      'setAudioStatus',
    ]),
    drawCircle: function (){
      window.requestAnimationFrame = window.requestAnimationFrame || window.mozRequestAnimationFrame || window.webkitRequestAnimationFrame || window.msRequestAnimationFrame

      this.canvas = this.$refs.arc
      // console.log(this.canvas)
      this.context = this.canvas.getContext('2d')
      this.x = this.canvas.width / 2
      this.y = this.canvas.height / 2

      this.context.lineWidth = 15
      this.context.strokeStyle = '#ffccd6'
      this.context.shadowOffsetX = 0
      this.context.shadowOffsetY = 0
      this.context.shadowBlur = 0
      this.context.shadowColor = 'transparent'
    },

    setDisplay: function(type){
      this.displayType = type
    },

		togglePlaylist: function() {
			this.isPlaylistActive = !this.isPlaylistActive;
		},

    togglePlayButton: function () {
      if (!this.currentlyPlaying) {
        this.playAudio()
      } else {
        this.pauseAudio()
      }
    },

    nextSong: function() {
			if (this.currentSong < this.playlist.length - 1){
        this.currentSong++;
        this.$nextTick(function(){
          this.playAudio()
        })
      }
		},

    prevSong: function() {
			if (this.currentSong > 0) {
        this.currentSong--;
        this.$nextTick(function(){
          this.playAudio()
        })
      }
    },

    resetPlayer: function (){
      this.audio = []
      this.totalDuration = 0
      this.currentlyPlaying=false
      this.currentlyStopped=true
      this.currentTime=0
      this.checkingCurrentPositionInTrack="",
      this.trackDuration=[],
      this.totalDuration=0,
      this.proceededTime=0,
      this.currentProgressBar=0,
      this.currentSong=0
      this.context.clearRect(0, 0, this.canvas.width, this.canvas.height)
    },

    loadSongs: function (){
      this.resetPlayer()
      this.playlist.forEach((song, index) => {

        this.audio[index] = new Audio(this.playlist[index].url);

        const localThis = this;
        this.audio[index].addEventListener("loadedmetadata", function() {
          if(this.duration === 'Infinity'){
            localThis.trackDuration[index] = Math.round(localThis.playlist[index].duration)
            localThis.totalDuration += Math.round(localThis.playlist[index].duration)
          } else {
            localThis.trackDuration[index] = Math.round(this.duration)
            localThis.totalDuration += Math.round(this.duration)
          }
        });
        this.audio[index].addEventListener("ended", this.handleEnded);
        this.audio[index].loop = false
      })
    },

		isCurrentSong: function(index) {
			if (this.currentSong === index) {
				return true;
			}
			return false;
    },

		getCurrentSong: function(currentSong) {
			return this.playlist[currentSong].url;
    },

		playAudio: function() {
      this.getCurrentTimeEverySecond();
      this.setAudioStatus({ playing: false })
      this.currentlyStopped = false;

      const self = this
      this.$nextTick(function(){
        self.setAudioStatus({ playing: true })
        self.audio[this.currentSong].play();
        self.currentlyPlaying = true
      })
    },

		pauseAudio: function() {
			this.audio[this.currentSong].pause();
			this.currentlyPlaying = false;
      clearTimeout(this.checkingCurrentPositionInTrack);
      this.setAudioStatus({ playing: false })
    },

    stopAudio: function() {
      this.audio[this.currentSong].pause()
      this.audio[this.currentSong].currentTime = 0;
      this.currentlyPlaying = false
      this.currentlyStopped = true
      this.currentSong = 0
      this.proceededTime = 0
      clearTimeout(this.checkingCurrentPositionInTrack)
      this.setAudioStatus({ playing: false })
    },

		handleEnded: function() {
			if (this.currentSong + 1 === this.playlist.length) {
        this.stopAudio()
			} else {
        this.proceededTime = this.trackDuration[this.currentSong];
				this.currentSong++
        setTimeout(() => {
          this.playAudio()
        }, this.interval)
      }
      this.context.clearRect(0, 0, this.canvas.width, this.canvas.height)
    },

		getCurrentTimeEverySecond: function() {
			const localThis = this;
			this.checkingCurrentPositionInTrack = setTimeout(function() {
        if(localThis.displayType === 'track time'){
          localThis.currentTime = localThis.audio[localThis.currentSong].currentTime;
          localThis.currentProgressBar = localThis.currentTime / localThis.trackDuration[localThis.currentSong] * 100;
        } else if(localThis.displayType === 'total time') {
          localThis.currentTime = localThis.proceededTime + localThis.audio[localThis.currentSong].currentTime;
          localThis.currentProgressBar = localThis.currentTime / localThis.totalDuration * 100;
        } else {
          localThis.currentTime = localThis.audio[localThis.currentSong].currentTime;
          localThis.currentProgressBar = localThis.currentTime / localThis.trackDuration[localThis.currentSong] * 100;
        }

        if (localThis.currentProgressBar < localThis.endPercent) {
          requestAnimationFrame(function () {
            localThis.animate(localThis.currentProgressBar / 100)
          });
        }
        localThis.getCurrentTimeEverySecond();
      }, 100);
    },

		toggleDebug: function(){
			this.debug=!this.debug;
			document.body.classList.toggle('debug');
    },

    animate: function(current) {
      this.context.clearRect(0, 0, this.canvas.width, this.canvas.height)
      this.context.beginPath()
      this.context.arc(this.x, this.y, this.radius, -(this.quart), ((this.circ) * current) - this.quart, false)
      this.context.stroke()
      this.context.closePath()
      if(this.currentlyStopped){
        this.context.clearRect(0, 0, this.canvas.width, this.canvas.height)
      }
    }
  },

}
</script>
<style lang="sass">
.audioPlayer
  position: relative
  margin: -1rem 0
  .arc
    position: relative
    width: 58px
    height: 58px
    border-radius: 50%
    background-color: darken($red, 0%)
  .playerButtons
    position: absolute
    right: 5px
    top: 5px
    overflow: hidden
    user-select: none
    display: flex
    justify-content: center
    align-items: center
    font-size: 30px
    text-decoration: none
    color: $white
    background-color: $red
    border-radius: 50%
    width: 48px
    height: 48px
    outline: 0
    cursor: pointer
    transition: 0.5s
    // border: solid 2px rgba(255, 255, 255, 1)
    border: none
    &.stopped
    &.pausing, &.playing
      color: $red
      background-color: $white

.text-size-medium
  .audioPlayer
    transform: scale(.85)
.text-size-small
  .audioPlayer
    transform: scale(.7);

/* data change transitions */
.slide-fade-enter-active
	transition: all 0.3s ease

.slide-fade-leave-active
	transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1)

.slide-fade-enter,
.slide-fade-leave-to
	transform: translateY(10px)
	opacity: 0


.fade-enter-active,
.fade-leave-active
	transition: opacity 0.5s

.fade-enter,
.fade-leave-to
	opacity: 0

</style>
